import { gql } from '@apollo/client'
import { useLazyQuery, useMutation } from '@apollo/client/react'
import { Trans, useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { parseCookies, setCookie } from 'nookies'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { discardApolloCacheOnNextUpdate } from 'src/context/ApolloProvider'
import useCacheUpdater from 'src/hooks/useCacheUpdater'
import useLazyFirebaseAuth from 'src/hooks/useLazyFirebaseAuth'
import useUserLocation from 'src/hooks/useUserLocation'
import { RedirectUrls } from 'src/types/types'
import { inferInterest } from 'src/utils/inferInterest'
import { identifyUser } from 'src/utils/pixels/common'
import ga from 'src/utils/pixels/gtag'
import {
  clearBrowserInternal,
  markBrowserInternal,
  setInternalTraffic
} from 'src/utils/pixels/internal'
import posthog from 'src/utils/pixels/posthog'
import { authEvent } from 'src/utils/pixels/spotmeEvents'
import {
  HAD_SESSION_STORAGE_KEY,
  safeStorageRemove,
  safeStorageSet
} from 'src/utils/safeStorage'
import { removeTokenCookie } from '../../apollo-client'
import { USER_CHANGES_FRAGMENT, USER_FRAGMENT } from '../../constants/fragments'
import {
  AuthEmailType,
  IMutation,
  IQuery,
  IQueryCurrentUserArgs,
  IUser,
  Maybe
} from '../../types/graphql'
import useLogger from '../LoggerProvider/useLogger'
import { AuthContext, AuthModalTrigger, IAuthContext } from './index'

// Dynamic-imported so firebase/compat/app + firebase/compat/auth (pulled in
// transitively by the Login subtree) aren't bundled into AuthProvider's chunk.
// Static loader height matches the real form's minimum footprint so opening
// the modal doesn't produce a content jump when the chunk resolves.
const loginImporter = () => import('../../components/Login')
const Login = dynamic(loginImporter, {
  loading: () => (
    <div className="min-h-[400px] space-y-3 p-1">
      <div className="h-12 animate-pulse rounded-lg bg-cream-300" />
      <div className="h-12 animate-pulse rounded-lg bg-cream-300" />
      <div className="h-px bg-ink-100/15" />
      <div className="h-12 animate-pulse rounded-lg bg-cream-300" />
      <div className="h-12 animate-pulse rounded-lg bg-cream-300" />
    </div>
  )
})
const ModalDrawer = dynamic(() => import('src/components/ModalDrawer'))
const CookieConsentPopup = dynamic(
  () => import('src/components/Cookies/CookieConsent')
)

const CURRENT_USER = gql`
  ${USER_FRAGMENT}
  ${USER_CHANGES_FRAGMENT}
  query CurrentUser($referrer: String, $interest: Interest) {
    currentUser(referrer: $referrer, interest: $interest) {
      ...CoreUserFields
      currency
      language
      referrer
      referrerStatus
      preferedNotiMethod
      notiPreferences {
        genre
        disabledMethods
      }
      userChanges {
        ...UserChangesFields
      }
    }

    ipInfo {
      ip
      address {
        country
        state
        zipcode
        city
      }
      center {
        lat
        lng
      }
    }
  }
`

const SEND_AUTH_EMAIL = gql`
  mutation SendAuthEmail(
    $email: String!
    $emailType: AuthEmailType!
    $redirectUrl: String
  ) {
    sendAuthEmail(
      email: $email
      emailType: $emailType
      redirectUrl: $redirectUrl
    )
  }
`

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const router = useRouter()
  const { t } = useTranslation('auth')
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [cookiesDealt, setCookiesDealt] = useState<boolean>(false)
  const auth = useAuth()

  // Why the modal last opened — read by the auth_modal_open / _abandoned
  // events so drop-off at the login gate is attributable per funnel.
  const modalTriggerRef = useRef<AuthModalTrigger>('other')
  const toggleLoginModal = useCallback(
    (open?: boolean, trigger?: AuthModalTrigger) => {
      if (trigger) modalTriggerRef.current = trigger
      if (open !== undefined) setModalOpen(open)
      else setModalOpen(!modalOpen)
    },
    [modalOpen]
  )

  // Auth modal open / abandon funnel anchors. Today we only fire `sign_up`
  // / `login` on SUCCESS — without these two events we cannot distinguish
  // "user saw the auth wall and bailed" from "user never saw it". Carry
  // `pathname` so the modal-trigger surface is sliceable in PostHog (auth
  // gate from checkout vs nav "Iniciar sesión" click vs cross-page CTA).
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (modalOpen && !wasOpenRef.current) {
      wasOpenRef.current = true
      posthog.event('auth_modal_open', {
        pathname: router.pathname,
        is_authenticated: auth.isAuthenticated,
        trigger: modalTriggerRef.current
      })
      return
    }
    if (!modalOpen && wasOpenRef.current) {
      wasOpenRef.current = false
      // Closed WITHOUT auth flipping to true ⇒ user abandoned the modal.
      // The success path closes via the `auth.isAuthenticated` effect
      // below and already fires `sign_up` / `login` — no abandon event.
      if (!auth.isAuthenticated) {
        posthog.event('auth_modal_abandoned', {
          pathname: router.pathname,
          trigger: modalTriggerRef.current
        })
      }
    }
  }, [modalOpen, auth.isAuthenticated, router.pathname])

  useEffect(() => {
    if (auth.isAuthenticated) {
      setModalOpen(false)
    }
  }, [auth.isAuthenticated])

  // Prefetch the Login chunk (which transitively pulls firebase/compat) on
  // browser idle so the modal doesn't show the skeleton on click. Uses
  // requestIdleCallback where available, falls back to a short timeout.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const schedule =
      (
        window as unknown as {
          requestIdleCallback?: (cb: () => void) => number
        }
      ).requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1500))
    const id = schedule(() => {
      // Best-effort warm-up: on a flaky mobile connection the chunk fetch can
      // fail (ChunkLoadError). webpack drops the failed promise, so the real
      // import on click retries and surfaces any persistent failure there.
      loginImporter().catch(() => undefined)
    })
    return () => {
      const cancel = (
        window as unknown as {
          cancelIdleCallback?: (id: number) => void
        }
      ).cancelIdleCallback
      if (cancel && typeof id === 'number') cancel(id)
    }
  }, [])

  const value = useMemo(
    () => ({
      ...auth,
      toggleLoginModal,
      isLoginModalOpen: modalOpen,
      cookiesDealt
    }),
    [auth, toggleLoginModal, modalOpen, cookiesDealt]
  )

  return (
    <AuthContext.Provider value={value}>
      <CookieConsentPopup setCookiesDealt={setCookiesDealt} />
      <ModalDrawer
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        title={t('MODAL_TITLE')}
        customTitle={
          <div className="flex flex-col">
            <span className="font-display text-[10px] tracking-[0.22em] text-blue-800 uppercase md:text-[11px]">
              {t('MODAL_EYEBROW')}
            </span>
            <h2 className="mt-2 font-display text-[22px] leading-[1.15] font-medium tracking-[-0.01em] text-ink-700 md:text-[26px]">
              <Trans
                i18nKey="MODAL_HEADING"
                ns="auth"
                components={{
                  1: <span className="font-display text-blue-500 italic" />
                }}
              />
            </h2>
          </div>
        }
        width="520px"
        sx={{ maxWidth: '540px', p: 1 }}
      >
        <Login isModal />
      </ModalDrawer>
      {children}
    </AuthContext.Provider>
  )
}

export default React.memo(AuthProvider)

/**
 * Hook
 */

export const useAuth = (): Omit<
  IAuthContext,
  'toggleLoginModal' | 'isLoginModalOpen' | 'cookiesDealt'
> => {
  const router = useRouter()
  const logger = useLogger()
  const { userAddress } = useUserLocation()

  const { user, loading, error } = useLazyFirebaseAuth()

  const [referrer, setReferrer] = useState<string>()
  useEffect(() => {
    if (user) return
    if (!router.isReady) return

    const queryNanoId =
      typeof router.query.nid === 'string' ? (router.query.nid as string) : null

    if (queryNanoId) {
      setReferrer(queryNanoId)
      setCookie(null, 'referrer', queryNanoId, {
        maxAge: 30 * 24 * 60 * 60,
        path: '/'
      })
    } else {
      const storageNanoId = parseCookies(null).referrer
      if (storageNanoId) {
        setReferrer(storageNanoId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady])

  if (error) {
    logger.error('Error retrieving firebase auth.', error)
  }

  const [data, setData] = useState<
    Pick<IQuery, 'currentUser' | 'ipInfo'> | undefined
  >()
  const [
    getCurrentUser,
    {
      data: currentUserData,
      error: currentUserError,
      loading: loadingCurrentUser,
      refetch
    }
  ] = useLazyQuery<
    Pick<IQuery, 'currentUser' | 'ipInfo'>,
    IQueryCurrentUserArgs
  >(CURRENT_USER, {
    fetchPolicy: 'cache-and-network'
  })

  useEffect(() => {
    if (currentUserData) {
      setData(currentUserData as Pick<IQuery, 'currentUser' | 'ipInfo'>)
    }
  }, [currentUserData])

  useEffect(() => {
    if (currentUserError) {
      logger.error('Error retrieving currentUser', currentUserError)
    }
  }, [currentUserError, logger])

  const updateCurrentUser = useCacheUpdater<
    Pick<IQuery, 'currentUser'>,
    IQueryCurrentUserArgs
  >({
    query: CURRENT_USER,
    variables: { referrer }
  })

  const [sendAuthEmail, { loading: loadingSendAuthEmail }] = useMutation<
    Pick<IMutation, 'sendAuthEmail'>
  >(SEND_AUTH_EMAIL, {
    onError: (error) => logger.error('Error sending auth email', error)
  })

  useEffect(() => {
    if (loading) return
    if (user) {
      ga.setUser(user.uid)
    }
    // Always fetch: currentUser resolves to null without auth,
    // but ipInfo is always returned based on the request IP.
    // `interest` is only consumed server-side during first-touch register
    // (when no user exists for this Firebase UID); it's ignored on
    // subsequent calls, so it's safe to send unconditionally.
    getCurrentUser({
      variables: { referrer, interest: inferInterest(router.pathname) }
    })
  }, [getCurrentUser, user, referrer, loading, router.pathname])

  // Keep the httpOnly session cookie in sync with Firebase's current ID
  // token. onIdTokenChanged fires on sign-in, sign-out, AND automatic token
  // refresh (~every hour), so the cookie never goes stale without us
  // needing to re-POST on every GraphQL request. firebase/auth is
  // dynamic-imported so its ~115 kB doesn't ship inside AuthProvider's chunk.
  useEffect(() => {
    let unsubscribe: (() => void) | undefined
    let cancelled = false

    const init = async () => {
      await import('src/firebase/firebaseClient')
      const { getAuth } = await import('firebase/auth')
      if (cancelled) return
      const auth = getAuth()
      // `undefined` until the first event (the session restore on load), which
      // must NOT reset Apollo — only a real identity change does. Token
      // refreshes keep the same uid and are ignored.
      let lastUid: string | null | undefined
      unsubscribe = auth.onIdTokenChanged(async (fbUser) => {
        const uid = fbUser?.uid ?? null
        if (lastUid !== undefined && lastUid !== uid) {
          discardApolloCacheOnNextUpdate()
        }
        lastUid = uid
        try {
          if (fbUser) {
            // Marks "this browser had a session" so useAuthRedirect can show
            // the session-expired banner on /login. Cleared only on explicit
            // logout (a revoked session resolving to null must keep it).
            safeStorageSet('local', HAD_SESSION_STORAGE_KEY, '1')
            const token = await fbUser.getIdToken()
            await fetch('/api/session', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token }),
              credentials: 'include'
            })
          } else {
            removeTokenCookie()
          }
        } catch (err) {
          logger.warn('Failed to sync session cookie', err)
        }
      })
    }
    init()

    return () => {
      cancelled = true
      if (unsubscribe) unsubscribe()
    }
  }, [logger])

  useEffect(() => {
    if (!user) return
    if (typeof window === 'undefined') return
    if (!data?.currentUser) return

    if (data.currentUser.isAdmin) {
      // Tag the admin in PostHog BEFORE flipping the internal-traffic
      // flag — once `markBrowserInternal()` runs, every posthog.*
      // call (incl. identify + setPersonProperties) is short-circuited
      // by the `isInternalTraffic()` guard inside `runOrQueue`. So we
      // identify + mark first, then block further capture.
      //
      // The `$is_internal_user` person property is what the project's
      // `test_account_filters` rule reads to hide team browsing from
      // dashboards when "Filter out internal and test users" is on.
      posthog.identifyUser({
        id: data.currentUser.id,
        email: data.currentUser.email,
        phone: data.currentUser.phoneNumber
      })
      posthog.markInternalUser()

      // Persisted, not just in-memory: on the NEXT page load this is read back
      // before `posthog.init()` runs, so posthog-js never starts and its
      // autocapture ($autocapture / $pageview / $web_vitals / …) — which lives
      // inside the SDK, beyond any wrapper guard — stops at the source.
      markBrowserInternal()
      ga.getGtag()('set', 'traffic_type', 'internal')
      return
    }

    // A non-admin signed in here, so this browser is not a staff machine (or
    // stopped being one). Drop any cached verdict before identifying, or a
    // handed-down laptop would suppress a real user's analytics forever.
    clearBrowserInternal()
    identifyUser(data.currentUser, user, null)
  }, [user, data, userAddress])

  useEffect(() => {
    if (!user || !router.isReady) return
    if (!data?.currentUser) return

    if (data.currentUser.isDeactivated) {
      const isSuspendedPage = router.pathname.includes(RedirectUrls.Suspended)
      if (!isSuspendedPage) {
        router.push(RedirectUrls.Suspended)
      }
      return
    }
  }, [user, data, router])

  useEffect(() => {
    if (!user || !router.isReady) return

    let verified = false
    user.providerData.forEach((profile) => {
      if (profile?.providerId === 'phone') verified = true
      if (profile?.providerId === 'google.com') verified = true
      if (profile?.providerId === 'facebook.com') verified = true
      if (profile?.providerId === 'password') verified = user.emailVerified
    })

    const pathname = router.pathname
    const privatePaths = [
      RedirectUrls.Dashboard,
      RedirectUrls.DashboardRent,
      RedirectUrls.UserConfirmation
    ]
    const isPrivatePath = privatePaths.some((path) => pathname.includes(path))
    const isPathConfirm = pathname.includes(RedirectUrls.ConfirmEmail)
    if (!isPrivatePath || isPathConfirm) return
    if (verified) return

    logger.warn('Email not verified, redirecting to confirm-email', {
      user,
      pathname
    })
    const redirect = window.location.pathname + window.location.search
    const encodedRedirect = encodeURIComponent(redirect)
    const redirectQuery = redirect ? `?redirectC=${encodedRedirect}` : ''

    const emailVeriSent = parseCookies(null).emailVerificationSent
    if (emailVeriSent) {
      router.push(RedirectUrls.ConfirmEmail + redirectQuery)
      return
    }

    setCookie(null, 'emailVerificationSent', 'true', { maxAge: 60 })
    // Send via the backend mutation (branded U-N07 react-email) instead of
    // Firebase's stock template. `sendAuthEmail` has onError wired, so the
    // promise always resolves.
    if (user.email) authEvent('auth_verification_sent', { surface: 'page' })
    const sendPromise = user.email
      ? sendAuthEmail({
          variables: {
            email: user.email,
            emailType: AuthEmailType.Verification,
            redirectUrl: redirect
          }
        })
      : Promise.resolve()
    void sendPromise.finally(() =>
      router.push(RedirectUrls.ConfirmEmail + redirectQuery)
    )
  }, [user, router, logger, sendAuthEmail])

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      loading,
      loadingCurrentUser,
      currentUser: (user && data
        ? data.currentUser
        : undefined) as Maybe<IUser>,
      ipInfo: data?.ipInfo ?? null,
      sendAuthEmail: async (
        email: string,
        emailType: AuthEmailType,
        redirectUrl?: string
      ) => {
        if (emailType === AuthEmailType.Verification) {
          authEvent('auth_verification_sent', { surface: 'page' })
        } else if (emailType === AuthEmailType.ResetPassword) {
          authEvent('auth_reset_requested', { surface: 'page' })
        }
        return sendAuthEmail({ variables: { email, emailType, redirectUrl } })
      },
      loadingSendAuthEmail,
      logout: async () => {
        setData(undefined)
        setInternalTraffic(false)
        try {
          const { default: firebase } =
            await import('src/firebase/firebaseClient')
          await firebase.auth().signOut()
          removeTokenCookie()
          safeStorageRemove('local', HAD_SESSION_STORAGE_KEY)
        } catch (error) {
          logger.error('Error logging out', error)
        }
      },
      refetchCurrentUser: async () => {
        refetch()
      },
      updateCurrentUser,
      referrer,
      setReferrer
    }),
    [
      user,
      loading,
      loadingCurrentUser,
      data,
      updateCurrentUser,
      referrer,
      sendAuthEmail,
      loadingSendAuthEmail,
      logger,
      refetch
    ]
  )

  return value
}
